import { useState, useEffect, useRef } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { X, Send, MessageCircle, Calendar, Tag, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${window.location.origin}/api`;

export default function AdminUpdatesPanel({ group, adminMember, currentUserEmail, onClose }) {
  const [tasks, setTasks] = useState([]);
  const [messages, setMessages] = useState([]);
  const [chatText, setChatText] = useState("");
  const [activeTab, setActiveTab] = useState("tasks");
  const [selectedTask, setSelectedTask] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [sendingComment, setSendingComment] = useState(false);
  const [commentReads, setCommentReads] = useState({});
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (adminMember) {
      fetchMyTasks();
      fetchChat();
      fetchCommentReads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminMember]);

  // Poll for updates every 5 seconds
  useEffect(() => {
    if (!adminMember) return;
    const interval = setInterval(() => {
      fetchMyTasks();
      fetchChat();
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminMember]);

  const fetchMyTasks = async () => {
    try {
      const res = await fetch(`${API}/tasks`, { credentials: "include" });
      if (res.ok) {
        const allTasks = await res.json();
        // Filter to only show tasks for this specific group
        const groupTasks = allTasks.filter(t => t.group_id === group.group_id);
        const tasksWithComments = groupTasks.filter(t => t.comments && t.comments.length > 0);
        const tasksWithout = groupTasks.filter(t => !t.comments || t.comments.length === 0);
        setTasks([...tasksWithComments, ...tasksWithout]);
      }
    } catch (err) {
      console.error("Failed to fetch tasks:", err);
    }
  };

  const fetchCommentReads = async () => {
    try {
      const res = await fetch(`${API}/comment-reads`, { credentials: "include" });
      if (res.ok) setCommentReads(await res.json());
    } catch { /* silent */ }
  };

  const markTaskRead = async (taskId, commentCount) => {
    const readCount = commentReads[taskId] || 0;
    if (commentCount > readCount) {
      setCommentReads(prev => ({ ...prev, [taskId]: commentCount }));
      try {
        await fetch(`${API}/comment-reads/${taskId}`, { method: "POST", credentials: "include" });
      } catch { /* silent */ }
    }
  };

  const fetchChat = async () => {
    try {
      const res = await fetch(`${API}/groups/${group.group_id}/chat/${adminMember.member_id}`, {
        credentials: "include",
      });
      if (res.ok) setMessages(await res.json());
    } catch (err) {
      console.error("Failed to fetch chat:", err);
    }
  };

  const handleSendChat = async () => {
    if (!chatText.trim()) return;
    try {
      const res = await fetch(`${API}/groups/${group.group_id}/chat/${adminMember.member_id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text: chatText.trim() }),
      });
      if (res.ok) {
        const msg = await res.json();
        setMessages(prev => [...prev, msg]);
        setChatText("");
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      }
    } catch {
      toast.error("Failed to send");
    }
  };

  const handleAddComment = async (taskId) => {
    if (!commentText.trim()) return;
    setSendingComment(true);
    try {
      const res = await fetch(`${API}/tasks/${taskId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text: commentText.trim() }),
      });
      if (res.ok) {
        const updatedTask = await res.json();
        setTasks(prev => prev.map(t => t.task_id === taskId ? updatedTask : t));
        setSelectedTask(updatedTask);
        setCommentText("");
        toast.success("Comment added");
      }
    } catch {
      toast.error("Failed to add comment");
    } finally {
      setSendingComment(false);
    }
  };

  useEffect(() => {
    if (activeTab === "chat") {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, activeTab]);

  if (!adminMember) return null;

  const tasksWithComments = tasks.filter(t => t.comments && t.comments.length > 0);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="admin-updates-panel">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-lg flex flex-col dark" data-theme="dark" style={{ backgroundColor: '#1e2126', color: '#fff' }}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium text-white flex-shrink-0"
              style={{ backgroundColor: adminMember.color }}
            >
              {adminMember.initials}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-white truncate">
                {adminMember.name}
                <span className="ml-2 text-xs text-blue-400 font-medium">Admin</span>
              </h2>
              <p className="text-xs text-gray-500">{adminMember.email}</p>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg" data-testid="admin-panel-close">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mt-4">
            <button
              className={`text-sm font-medium pb-2 border-b-2 transition-colors ${
                activeTab === "tasks" ? "text-white border-blue-500" : "text-gray-500 border-transparent hover:text-gray-300"
              }`}
              onClick={() => setActiveTab("tasks")}
              data-testid="admin-tab-tasks"
            >
              Tasks ({tasksWithComments.length})
            </button>
            <button
              className={`text-sm font-medium pb-2 border-b-2 transition-colors ${
                activeTab === "chat" ? "text-white border-blue-500" : "text-gray-500 border-transparent hover:text-gray-300"
              }`}
              onClick={() => setActiveTab("chat")}
              data-testid="admin-tab-chat"
            >
              Chat ({messages.length})
            </button>
          </div>
        </div>

        {/* Tasks Tab - shows member's tasks with admin comments */}
        {activeTab === "tasks" && (
          <ScrollArea className="flex-1">
            <div className="p-4 space-y-2">
              {tasksWithComments.length === 0 && (
                <div className="text-center py-10">
                  <CheckCircle2 className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No admin comments on your tasks yet</p>
                </div>
              )}
              {tasksWithComments.map((task) => {
                const totalComments = task.comments?.length || 0;
                const readCount = commentReads[task.task_id] || 0;
                const unread = Math.max(0, totalComments - readCount);
                return (
                <div
                  key={task.task_id}
                  className={`p-3 rounded-lg border border-white/5 bg-white/5 cursor-pointer transition-colors hover:bg-white/[0.07] ${
                    selectedTask?.task_id === task.task_id ? "ring-1 ring-blue-500/50" : ""
                  }`}
                  onClick={() => {
                    const opening = selectedTask?.task_id !== task.task_id;
                    setSelectedTask(opening ? task : null);
                    setCommentText("");
                    if (opening && unread > 0) markTaskRead(task.task_id, totalComments);
                  }}
                  data-testid={`admin-update-task-${task.task_id}`}
                >
                  {/* Task Header */}
                  <div className="flex items-start gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${
                      task.completed ? "border-green-500 bg-green-500" : "border-gray-500"
                    }`}>
                      {task.completed && (
                        <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none">
                          <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${task.completed ? "text-gray-500 line-through" : "text-white"}`}>
                        {task.title || "Untitled"}
                      </p>
                      {task.description && (
                        <p className="text-xs text-gray-500 mt-1 truncate">{task.description}</p>
                      )}
                      {/* Comment count indicator */}
                      <div className="flex items-center gap-1 mt-1">
                        <MessageCircle className="w-3 h-3 text-blue-400" />
                        <span className="text-[10px] text-blue-400">{task.comments?.length} comment(s)</span>
                      </div>
                    </div>
                    {unread > 0 && (
                      <div className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0" data-testid={`unread-badge-${task.task_id}`}>
                        <span className="text-[10px] font-bold text-white">{unread}</span>
                      </div>
                    )}
                  </div>

                  {/* Task Details (expanded) */}
                  {selectedTask?.task_id === task.task_id && (
                    <div className="mt-3 pt-3 border-t border-white/5 space-y-2" onClick={(e) => e.stopPropagation()}>
                      {task.due_date && (
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>
                          {task.due_time && <span>at {task.due_time}</span>}
                        </div>
                      )}
                      {task.tags?.length > 0 && (
                        <div className="flex items-center gap-2">
                          <Tag className="w-3.5 h-3.5 text-gray-400" />
                          <div className="flex flex-wrap gap-1">
                            {task.tags.map((tag, i) => (
                              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-gray-400">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Comments section */}
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                          <MessageCircle className="w-3 h-3" />
                          Comments
                        </p>
                        {task.comments?.length > 0 && (
                          <div className="space-y-1.5 mb-2 max-h-48 overflow-y-auto">
                            {task.comments.map((c) => {
                              const isSelf = c.user_id && currentUserEmail && c.user_name?.toLowerCase().includes(currentUserEmail.split('@')[0].toLowerCase());
                              const isMine = c.user_email === currentUserEmail || (!c.user_email && c.user_name === adminMember?.name) === false;
                              // Simple check: if user_name matches adminMember name, it's admin; otherwise it's the member (self)
                              const isAdmin = c.user_name === adminMember?.name;
                              return (
                                <div key={c.id} className={`text-xs rounded p-2 ${isAdmin ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-emerald-500/10 border border-emerald-500/20'}`}>
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <span className={`font-medium ${isAdmin ? 'text-blue-400' : 'text-emerald-400'}`}>{c.user_name || "User"}</span>
                                    <span className="text-gray-600 text-[10px]">
                                      {new Date(c.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                                    </span>
                                  </div>
                                  <span className="text-gray-300">{c.text}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Member reply input */}
                        <div className="flex gap-2 mt-2">
                          <Input
                            data-testid={`member-comment-input-${task.task_id}`}
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            placeholder="Reply to admin..."
                            className="text-xs h-8"
                            style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: '#fff' }}
                            onKeyDown={(e) => { if (e.key === "Enter") handleAddComment(task.task_id); }}
                          />
                          <button
                            data-testid={`member-comment-send-${task.task_id}`}
                            onClick={() => handleAddComment(task.task_id)}
                            disabled={sendingComment || !commentText.trim()}
                            className="p-1.5 bg-blue-500 hover:bg-blue-600 disabled:opacity-40 rounded-lg transition-colors flex-shrink-0"
                          >
                            <Send className="w-3.5 h-3.5 text-white" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )})}
            </div>
          </ScrollArea>
        )}

        {/* Chat Tab */}
        {activeTab === "chat" && (
          <>
            <ScrollArea className="flex-1 p-4">
              {messages.length === 0 && (
                <div className="text-center py-10">
                  <MessageCircle className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No messages yet</p>
                </div>
              )}
              {messages.map((msg) => {
                const isSelf = msg.sender_email === currentUserEmail;
                return (
                  <div key={msg.msg_id} className={`flex mb-3 ${isSelf ? "justify-end" : "justify-start"}`}>
                    <div className="max-w-[75%]">
                      {!isSelf && (
                        <div className="flex items-center gap-2 mb-1">
                          <div
                            className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-medium text-white"
                            style={{ backgroundColor: msg.sender_color || "#6b7280" }}
                          >
                            {msg.sender_initials}
                          </div>
                          <span className="text-xs text-gray-500">{msg.sender_name}</span>
                        </div>
                      )}
                      <div className={`px-3 py-2 rounded-xl text-sm ${
                        isSelf
                          ? "bg-blue-500 text-white rounded-br-sm"
                          : "bg-white/10 text-white rounded-bl-sm"
                      }`}>
                        {msg.text}
                      </div>
                      <p className="text-[10px] text-gray-600 mt-0.5 px-1">
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </ScrollArea>

            {/* Chat Input */}
            <div className="p-4 border-t border-white/10">
              <div className="flex gap-2">
                <Input
                  data-testid="member-chat-input"
                  value={chatText}
                  onChange={(e) => setChatText(e.target.value)}
                  placeholder="Reply to admin..."
                  style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: '#fff' }}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSendChat(); }}
                />
                <button
                  data-testid="member-chat-send"
                  onClick={handleSendChat}
                  className="p-2.5 bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
                >
                  <Send className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
